const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const ABSOLUTE = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' });

export const formatRelativeTime = (isoTimestamp: string, now: number): string => {
  const then = new Date(isoTimestamp).getTime();

  if (!Number.isFinite(then)) {
    return 'unknown';
  }

  const elapsed = now - then;

  if (elapsed < MINUTE) {
    return 'just now';
  }
  if (elapsed < HOUR) {
    return RELATIVE.format(-Math.floor(elapsed / MINUTE), 'minute');
  }
  if (elapsed < DAY) {
    return RELATIVE.format(-Math.floor(elapsed / HOUR), 'hour');
  }
  if (elapsed < 7 * DAY) {
    return RELATIVE.format(-Math.floor(elapsed / DAY), 'day');
  }
  return ABSOLUTE.format(then);
};

// Clamps at "0:00" rather than letting a stale progress value render negative.
export const formatDuration = (durationMs: number): string => {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
};

const CLOCK = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

// Seconds are shown because the pair's whole point is the window's length; the server
// records to the second and no finer.
export const formatClockRange = (fromIso: string, toIso: string): string => {
  const from = new Date(fromIso);
  const to = new Date(toIso);

  if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime())) {
    return 'unknown';
  }

  return `${CLOCK.format(from)}–${CLOCK.format(to)}`;
};

export const initialsOf = (displayName: string): string => {
  const words = displayName
    .trim()
    .split(/[\s_-]+/u)
    .filter(Boolean);
  const first = words[0]?.[0] ?? '?';
  const second = words.length > 1 ? (words.at(-1)?.[0] ?? '') : '';
  return `${first}${second}`.toUpperCase();
};

export { formatStartsIn } from '@mixwire/contracts';
